/**
 * The EMTAK / NACE Rev. 2 activity catalogue (the activity filter).
 *
 * In the business register's open data `emtak` is a 5-digit code following the
 * NACE structure:
 *   section (letter A-U) -> division (2 digits) -> group (3) -> class (4) -> EMTAK (5).
 * The filter sends the backend an `emtak` parameter as comma-separated CODE
 * PREFIXES:
 *   - a division -> one prefix (for example "62")
 *   - a section -> all of its divisions (for example "10,11,...,33")
 *   - an activity class -> the codes tied to that name (for example "9319,93199"),
 *       see `EMTAK_ACTIVITIES` (level 3)
 * The backend turns every prefix into a `startsWith` OR (see buildWhere).
 *
 * The three filter levels (section -> division -> activity) are flattened into
 * one combobox list, `EMTAK_ACTIVITY_OPTIONS`. Sections and divisions are
 * curated by hand below; the activity classes are GENERATED from real data into
 * `emtak-activities.ts` (see its header). Regenerate that file after a new
 * import if the filter should offer the new activities.
 */

import { EMTAK_ACTIVITIES } from "./emtak-activities";

export interface EmtakSection {
  /** Section letter (A-U). */
  letter: string;
  /** Section name (Estonian). */
  name: string;
  /** Divisions: a 2-digit code plus a name. */
  divisions: { code: string; name: string }[];
}

export const EMTAK_SECTIONS: EmtakSection[] = [
  {
    letter: "A",
    name: "Põllumajandus, metsamajandus ja kalapüük",
    divisions: [
      { code: "01", name: "Taime- ja loomakasvatus, jahindus" },
      { code: "02", name: "Metsamajandus ja metsavarumine" },
      { code: "03", name: "Kalapüük ja vesiviljelus" },
    ],
  },
  {
    letter: "B",
    name: "Mäetööstus",
    divisions: [
      { code: "05", name: "Kivi- ja pruunsöe kaevandamine" },
      { code: "06", name: "Toornafta ja maagaasi tootmine" },
      { code: "07", name: "Metallimaakide kaevandamine" },
      { code: "08", name: "Muu kaevandamine" },
      { code: "09", name: "Kaevandamist abistavad tegevusalad" },
    ],
  },
  {
    letter: "C",
    name: "Töötlev tööstus",
    divisions: [
      { code: "10", name: "Toiduainete tootmine" },
      { code: "11", name: "Joogitootmine" },
      { code: "12", name: "Tubakatoodete tootmine" },
      { code: "13", name: "Tekstiilitootmine" },
      { code: "14", name: "Rõivatootmine" },
      { code: "15", name: "Nahatöötlemine ja nahktoodete tootmine" },
      { code: "16", name: "Puidutöötlemine ja puittoodete tootmine" },
      { code: "17", name: "Paberi ja pabertoodete tootmine" },
      { code: "18", name: "Trükindus ja salvestiste paljundus" },
      { code: "19", name: "Koksi ja puhastatud naftatoodete tootmine" },
      { code: "20", name: "Kemikaalide ja keemiatoodete tootmine" },
      { code: "21", name: "Põhifarmaatsiatoodete ja ravimite tootmine" },
      { code: "22", name: "Kummi- ja plasttoodete tootmine" },
      { code: "23", name: "Muude mittemetalsetest mineraalidest toodete tootmine" },
      { code: "24", name: "Metallitootmine" },
      { code: "25", name: "Metalltoodete tootmine (v.a masinad)" },
      { code: "26", name: "Arvutite, elektroonika- ja optikaseadmete tootmine" },
      { code: "27", name: "Elektriseadmete tootmine" },
      { code: "28", name: "Mujal liigitamata masinate ja seadmete tootmine" },
      { code: "29", name: "Mootorsõidukite ja haagiste tootmine" },
      { code: "30", name: "Muude transpordivahendite tootmine" },
      { code: "31", name: "Mööblitootmine" },
      { code: "32", name: "Muu tootmine" },
      { code: "33", name: "Masinate ja seadmete remont ja paigaldus" },
    ],
  },
  {
    letter: "D",
    name: "Elektrienergia, gaasi ja auruga varustamine",
    divisions: [{ code: "35", name: "Elektrienergia, gaasi, auru ja kliimaga varustamine" }],
  },
  {
    letter: "E",
    name: "Veevarustus; kanalisatsioon ja jäätmekäitlus",
    divisions: [
      { code: "36", name: "Veekogumine, -töötlus ja -varustus" },
      { code: "37", name: "Kanalisatsioon" },
      { code: "38", name: "Jäätmekogumine, -töötlus ja -kõrvaldus" },
      { code: "39", name: "Saastekäitlus jm jäätmekäitlus" },
    ],
  },
  {
    letter: "F",
    name: "Ehitus",
    divisions: [
      { code: "41", name: "Hoonete ehitus" },
      { code: "42", name: "Rajatiste ehitus" },
      { code: "43", name: "Eriehitustööd" },
    ],
  },
  {
    letter: "G",
    name: "Hulgi- ja jaekaubandus; mootorsõidukite remont",
    divisions: [
      { code: "45", name: "Mootorsõidukite ja mootorrataste müük ja remont" },
      { code: "46", name: "Hulgikaubandus (v.a mootorsõidukid)" },
      { code: "47", name: "Jaekaubandus (v.a mootorsõidukid)" },
    ],
  },
  {
    letter: "H",
    name: "Veondus ja laondus",
    divisions: [
      { code: "49", name: "Maismaaveondus ja torutransport" },
      { code: "50", name: "Veetransport" },
      { code: "51", name: "Õhutransport" },
      { code: "52", name: "Laondus ja veondust abistavad tegevusalad" },
      { code: "53", name: "Posti- ja kulleriteenistus" },
    ],
  },
  {
    letter: "I",
    name: "Majutus ja toitlustus",
    divisions: [
      { code: "55", name: "Majutus" },
      { code: "56", name: "Toidu ja joogi serveerimine" },
    ],
  },
  {
    letter: "J",
    name: "Info ja side",
    divisions: [
      { code: "58", name: "Kirjastamine" },
      { code: "59", name: "Kino-, video- ja telesaadete tootmine; muusika" },
      { code: "60", name: "Programmid ja ringhääling" },
      { code: "61", name: "Telekommunikatsioon" },
      { code: "62", name: "Programmeerimine, konsultatsioonid jms" },
      { code: "63", name: "Infoalane tegevus" },
    ],
  },
  {
    letter: "K",
    name: "Finants- ja kindlustustegevus",
    divisions: [
      { code: "64", name: "Finantsteenuste osutamine (v.a kindlustus)" },
      { code: "65", name: "Kindlustus, edasikindlustus ja pensionifondid" },
      { code: "66", name: "Finants- ja kindlustustegevuse abitegevused" },
    ],
  },
  {
    letter: "L",
    name: "Kinnisvaraalane tegevus",
    divisions: [{ code: "68", name: "Kinnisvaraalane tegevus" }],
  },
  {
    letter: "M",
    name: "Kutse-, teadus- ja tehnikaalane tegevus",
    divisions: [
      { code: "69", name: "Juriidilised toimingud ja arvepidamine" },
      { code: "70", name: "Peakontorite tegevus; juhtimisalane nõustamine" },
      { code: "71", name: "Arhitekti- ja inseneritegevused; teimimine" },
      { code: "72", name: "Teadus- ja arendustegevus" },
      { code: "73", name: "Reklaamindus ja turu-uuringud" },
      { code: "74", name: "Muu kutse-, teadus- ja tehnikaalane tegevus" },
      { code: "75", name: "Veterinaaria" },
    ],
  },
  {
    letter: "N",
    name: "Haldus- ja abitegevused",
    divisions: [
      { code: "77", name: "Rentimine ja kasutusrent" },
      { code: "78", name: "Tööhõive" },
      { code: "79", name: "Reisibürood ja reisikorraldajad" },
      { code: "80", name: "Turvatöö ja juurdlus" },
      { code: "81", name: "Hoonete ja maastike hooldus" },
      { code: "82", name: "Büroohaldus ja muu äritegevuse abitegevused" },
    ],
  },
  {
    letter: "O",
    name: "Avalik haldus ja riigikaitse",
    divisions: [{ code: "84", name: "Avalik haldus ja riigikaitse; sotsiaalkindlustus" }],
  },
  {
    letter: "P",
    name: "Haridus",
    divisions: [{ code: "85", name: "Haridus" }],
  },
  {
    letter: "Q",
    name: "Tervishoid ja sotsiaalhoolekanne",
    divisions: [
      { code: "86", name: "Tervishoid" },
      { code: "87", name: "Hoolekandeasutuste tegevus" },
      { code: "88", name: "Sotsiaalhoolekanne ilma majutuseta" },
    ],
  },
  {
    letter: "R",
    name: "Kunst, meelelahutus ja vaba aeg",
    divisions: [
      { code: "90", name: "Loome-, kunsti- ja meelelahutustegevus" },
      { code: "91", name: "Raamatukogud, arhiivid, muuseumid" },
      { code: "92", name: "Hasartmängude ja kihlvedude korraldamine" },
      { code: "93", name: "Sport ning lõbustus- ja vaba aja tegevused" },
    ],
  },
  {
    letter: "S",
    name: "Muud teenindavad tegevused",
    divisions: [
      { code: "94", name: "Organisatsioonide tegevus" },
      { code: "95", name: "Arvutite ja tarbeesemete parandus" },
      { code: "96", name: "Muu teenindus" },
    ],
  },
  {
    letter: "T",
    name: "Kodumajapidamiste kui tööandjate tegevus",
    divisions: [
      { code: "97", name: "Kodumajapidamiste kui tööandjate tegevus" },
      { code: "98", name: "Kodumajapidamiste oma tarbeks tootmine" },
    ],
  },
  {
    letter: "U",
    name: "Eksterritoriaalsete organisatsioonide tegevus",
    divisions: [{ code: "99", name: "Eksterritoriaalsete organisatsioonide tegevus" }],
  },
];

export interface ActivityOption {
  /** Comma-separated EMTAK code prefixes (for the backend). */
  value: string;
  /** Display name (the combobox label). */
  label: string;
  /** Helper line (the combobox hint): section / code. */
  hint: string;
  /** Hierarchy level: 0 = section, 1 = division, 2 = activity class. */
  level: 0 | 1 | 2;
}

const RAW_ACTIVITY_OPTIONS: ActivityOption[] = EMTAK_SECTIONS.flatMap((s) => [
  {
    value: s.divisions.map((d) => d.code).join(","),
    label: s.name,
    hint: `Sektsioon ${s.letter} · kogu valdkond`,
    level: 0 as const,
  },
  ...s.divisions.flatMap((d) => [
    {
      value: d.code,
      label: d.name,
      hint: `${d.code} · sektsioon ${s.letter}`,
      level: 1 as const,
    },
    ...(EMTAK_ACTIVITIES[d.code] ?? []).map((a) => ({
      value: a.v,
      label: a.l,
      hint: `${d.name} · ${a.v}`,
      level: 2 as const,
    })),
  ]),
]);

/**
 * A flat combobox list covering three levels: one "whole field" row per section,
 * then its divisions, then each division's generated activity classes. Search
 * matches on a section, a division or an activity name alike, so typing part of
 * an activity name finds the class that contains it.
 *
 * Dedup on value plus label keeps the list unique: in single-division sections
 * (L/68, P/85, U/99) the section row coincides with its division, and some
 * 2-digit class codes collide with a division. The first occurrence wins
 * (section, then division, then class) and the rest drop out.
 */
export const EMTAK_ACTIVITY_OPTIONS: ActivityOption[] = (() => {
  const seen = new Set<string>();
  return RAW_ACTIVITY_OPTIONS.filter((o) => {
    const k = `${o.value}\u0000${o.label}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
})();

/** An EMTAK filter value to a human-readable label (for the active filter chip). */
export function activityLabel(value: string | undefined): string {
  if (!value) return "";
  return EMTAK_ACTIVITY_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/** A 2-digit division code to its section (letter plus name). The activity
 *  statistics roll the 2-digit divisions up into 21 readable sections. */
export const EMTAK_DIVISION_SECTION: Record<string, { letter: string; name: string }> = (() => {
  const m: Record<string, { letter: string; name: string }> = {};
  for (const s of EMTAK_SECTIONS) for (const d of s.divisions) m[d.code] = { letter: s.letter, name: s.name };
  return m;
})();

/** A 2-digit division to its section; unknown or empty gives null. */
export function sectionForDivision(div: string | null | undefined): { letter: string; name: string } | null {
  if (!div) return null;
  return EMTAK_DIVISION_SECTION[div] ?? null;
}
